import { getQuestScheduleDateKey } from './questScheduleDate';
import { getLocalDateKey } from './questScheduleDate';

const HEALTHKIT_DEFINITIONS = {
  steps: {
    key: 'steps',
    targetValue: 10000,
  },
  sleep: {
    key: 'sleep',
    targetValue: 420,
  },
};

const HEALTHKIT_ORDER = ['steps', 'sleep'];

const formatCount = (value) => new Intl.NumberFormat('en-US').format(Math.max(0, Math.round(value || 0)));

export const formatHealthKitProgressLabel = (key, currentValue, targetValue) => {
  const resolvedCurrent = Math.max(0, Math.round(currentValue || 0));
  const resolvedTarget = Math.max(0, Math.round(targetValue || 0));

  if (key === 'steps') {
    return `${formatCount(resolvedCurrent)} / ${formatCount(resolvedTarget)} steps`;
  }

  return `${formatCount(resolvedCurrent)} / ${formatCount(resolvedTarget)} min`;
};

const resolveHealthKitStatusText = ({ slide, snapshot, t }) => {
  if (slide.isLockedTeaser) {
    return t('playerStats.healthKitEnableStatus', 'Enable HealthKit verification in Settings on iPhone');
  }
  if (slide.completed) {
    return t('playerStats.healthKitCompleted', 'Completed');
  }
  if (!snapshot?.lastSyncedAt) {
    return t('playerStats.healthKitWaiting', 'Waiting for HealthKit sync');
  }
  return t('playerStats.healthKitInProgress', 'In progress');
};

export const buildHealthKitSlides = ({
  quests = [],
  teaserQuests = [],
  snapshot = null,
  todayDateKey,
  now = new Date(),
  fallbackTimezone = 'UTC',
  t = (key, fallback) => fallback,
}) => {
  const todaysHealthKitQuests = quests.filter((quest) =>
    quest?.type === 'HealthKit'
    && getQuestScheduleDateKey(quest) === (
      quest?.userTimezoneAtScheduling
        ? getLocalDateKey(now, quest.userTimezoneAtScheduling || fallbackTimezone)
        : todayDateKey
    )
  );
  const todaysHealthKitTeasers = teaserQuests.filter((quest) =>
    quest?.type === 'HealthKit'
    && getQuestScheduleDateKey(quest) === (
      quest?.userTimezoneAtScheduling
        ? getLocalDateKey(now, quest.userTimezoneAtScheduling || fallbackTimezone)
        : todayDateKey
    )
  );

  return HEALTHKIT_ORDER.map((key) => {
    const definition = HEALTHKIT_DEFINITIONS[key];
    const sourceQuest = todaysHealthKitQuests.find((quest) => quest?.detailedDescription?.healthkit?.key === key)
      || todaysHealthKitTeasers.find((quest) => quest?.detailedDescription?.healthkit?.key === key)
      || null;
    const healthkit = sourceQuest?.detailedDescription?.healthkit || {};
    const currentValue = Number.isFinite(healthkit.currentValue) ? Math.max(0, Math.round(healthkit.currentValue)) : 0;
    const targetValue = Number.isFinite(healthkit.targetValue) ? Math.max(0, Math.round(healthkit.targetValue)) : definition.targetValue;
    const progressLabel = healthkit.progressLabel || formatHealthKitProgressLabel(key, currentValue, targetValue);
    const completed = Boolean(sourceQuest?.completed);
    const isLockedTeaser = Boolean(sourceQuest?.isLockedTeaser);

    const slide = {
      key,
      title: key === 'steps'
        ? t('playerStats.healthKitStepsTitle', '10,000 Steps Goal')
        : t('playerStats.healthKitSleepTitle', '7 Hours Sleep Goal'),
      currentValue,
      targetValue,
      progressLabel,
      valueLabel: isLockedTeaser ? t('playerStats.locked', 'Locked') : progressLabel,
      completed,
      isLockedTeaser,
      statusText: '',
      progressPercent: isLockedTeaser ? 0 : Math.min((currentValue / (targetValue || 1)) * 100, 100),
      lastSyncedAt: snapshot?.lastSyncedAt || null,
    };

    slide.statusText = resolveHealthKitStatusText({ slide, snapshot, t });
    return slide;
  });
};

export const buildPlayerStatsSlideDescriptors = ({
  hasNutritionSlide = false,
  healthkitSlides = [],
} = {}) => {
  const orderedHealthKitSlides = HEALTHKIT_ORDER
    .map((key) => healthkitSlides.find((slide) => slide?.key === key))
    .filter(Boolean);

  const slides = [{ key: 'main', kind: 'main' }];

  if (hasNutritionSlide) {
    slides.push({ key: 'nutrition', kind: 'nutrition' });
  }

  slides.push({ key: 'calories', kind: 'calories' });

  orderedHealthKitSlides.forEach((slide) => {
    slides.push({
      key: `healthkit-${slide.key}`,
      kind: 'healthkit',
      data: slide,
    });
  });

  return slides;
};
